import React, { useState, useEffect } from 'react';
import { User, Bell, Menu, Edit, Eye, Download, Share2, Activity, Lock, Star, CheckCircle, MessageCircle, Users, FileText, TrendingUp, Mail as MailIcon, Phone as PhoneIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../Pages/Navbar/JobSeekerNavbar';
// Static imports removed for dynamic import in handleDownloadCV
import BaseUrl from '../../ApiServices/BaseUrl'; // Import your BaseUrl

const CandidateDashboard = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const navigate = useNavigate();

  // Add this state variable
const [profilesVisited, setProfilesVisited] = useState({ today: 0, total: 0 });


  // Add this with other state declarations
const [messageCount, setMessageCount] = useState(0);
const [loadingMessages, setLoadingMessages] = useState(false);

// Add this near the top of your component, with other state declarations
const [profileUpdatedDate, setProfileUpdatedDate] = useState('');
// Add with other state declarations
const [applicationsData, setApplicationsData] = useState([]);
const [loadingApplications, setLoadingApplications] = useState(false);
const [employerJobs, setEmployerJobs] = useState([]);
const [profileViewers, setProfileViewers] = useState([]);
const [viewersLoading, setViewersLoading] = useState(true);
const [currentSub, setCurrentSub] = useState(null);




// Add this useEffect to fetch the created_at date from localStorage
useEffect(() => {
  const fetchProfileUpdatedDate = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      if (user.created_at) {
        // Format the date to display like "Apr 24"
        const date = new Date(user.created_at);
        const formattedDate = date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
        setProfileUpdatedDate(formattedDate);
      }
    } catch (error) {
      console.error('Error fetching profile update date:', error);
    }
  };

  fetchProfileUpdatedDate();
}, []);


// Add this useEffect to fetch messages
useEffect(() => {
  const fetchMessageCount = async () => {
    try {
      setLoadingMessages(true);
      const user = JSON.parse(localStorage.getItem("user")) || {};
      const userId = user.id || '';
      
      if (!userId) {
        setMessageCount(0);
        return;
      }

      const response = await fetch(`${BaseUrl}/message/get-all`);
      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }
      
      const messages = await response.json();
      
      // Filter messages where job_seeker_id matches current user's ID
      const userMessages = messages.filter(message => 
        message.job_seeker_id && parseInt(message.job_seeker_id) === parseInt(userId)
      );
      
      setMessageCount(userMessages.length);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessageCount(0);
    } finally {
      setLoadingMessages(false);
    }
  };

  fetchMessageCount();
}, []);


// Add this useEffect to fetch profiles visited count
useEffect(() => {
  const fetchProfilesVisited = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      const userId = user.id || '';
      
      if (!userId) return;

      const response = await fetch(`${BaseUrl}/api/profiles-visited/${userId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch profiles visited count');
      }
      
      const data = await response.json();
      setProfilesVisited({
        today: data.today || 0,
        total: data.total || 0
      });
      
    } catch (error) {
      console.error('Error fetching profiles visited:', error);
    }
  };

  fetchProfilesVisited();
}, []);


// Add this useEffect to fetch employer jobs
useEffect(() => {
  const fetchEmployerJobs = async () => {
    try {
      const response = await fetch(`${BaseUrl}/api/employer/all`);
      if (!response.ok) {
        throw new Error('Failed to fetch employer jobs');
      }
      const data = await response.json();
      setEmployerJobs(data);
    } catch (error) {
      console.error('Error fetching employer jobs:', error);
      setEmployerJobs([]);
    }
  };

  fetchEmployerJobs();
  fetchCurrentSub();
  fetchProfileViewers();
}, []);

const fetchCurrentSub = async () => {
  try {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    const userId = user.id || '';
    if (!userId) return;
    const response = await fetch(`${BaseUrl}/api/candidate/subscription/${userId}`);
    if (response.ok) {
      const data = await response.json();
      setCurrentSub(data.subscription);
    }
  } catch (error) {
    console.error('Error fetching current subscription:', error);
  }
};

const fetchProfileViewers = async () => {
  try {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    const userId = user.id || '';
    if (!userId) return;
    const response = await fetch(`${BaseUrl}/api/profile-viewers/${userId}`);
    if (response.ok) {
      const data = await response.json();
      setProfileViewers(data.data || []);
    }
  } catch (error) {
    console.error('Error fetching profile viewers:', error);
  } finally {
    setViewersLoading(false);
  }
};

const isPremiumPlan = () => {
  const name = currentSub?.plan_name?.toLowerCase() || "";
  return name.includes('silver') || name.includes('gold');
};

const handleLockedFeatureClick = () => {
  navigate('/candidate-subscription');
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

// Add this useEffect to fetch applications data
useEffect(() => {
  const fetchApplications = async () => {
    try {
      setLoadingApplications(true);
      const user = JSON.parse(localStorage.getItem("user")) || {};
      const userId = user.id || '';
      
      if (!userId) {
        console.log('User ID not found');
        return;
      }

      // Fetch applications for current user
      const response = await fetch(`${BaseUrl}/api/applications?userid=${userId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch applications');
      }
      
      const applications = await response.json();
      setApplicationsData(applications);
      
    } catch (error) {
      console.error('Error fetching applications:', error);
      setApplicationsData([]);
    } finally {
      setLoadingApplications(false);
    }
  };

  fetchApplications();
}, []);



  // State for share modal
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareContent, setShareContent] = useState('');
  const [shareUrl, setShareUrl] = useState('');

  // Helper function to get job title from employer jobs
const getJobTitleByJobId = (jobid) => {
  if (!employerJobs || employerJobs.length === 0) {
    return `Job ID: ${jobid}`;
  }
  
  const job = employerJobs.find(empJob => empJob.id === jobid);
  return job ? (job.job_title || `Job at ${job.emp_name || 'Employer'}`) : `Job ID: ${jobid}`;
};

  // Function to handle sharing
  const handleShareCV = () => {
    // Get user data (you might want to fetch this if not available)
    const userData = profileData || JSON.parse(localStorage.getItem("user")) || {};
    
    const candidateName = `${userData.first_name || 'Candidate'} ${userData.last_name || ''}`;
    const jobPosition = userData.job_position || 'Domestic Worker';
    const nationality = userData.nationality || '';
    const age = userData.age ? `${userData.age} years` : '';
    const experience = userData.total_work_experience || 'Not specified';
    
    const shareText = `Check out ${candidateName}'s CV - ${jobPosition} | ${nationality} | ${age} | ${experience} experience. Find more candidates on Gulf Worker.`;
    const shareUrlVal = `${window.location.origin}/combinedprofilecard`;
    const fullShareContent = `${shareText} ${shareUrlVal}`;
    
    if (navigator.share) {
      navigator.share({
        title: `${candidateName}'s CV`,
        text: shareText,
        url: shareUrlVal,
      }).catch((error) => console.log('Error sharing:', error));
    } else {
      setShareContent(fullShareContent);
      setShareUrl(shareUrlVal);
      setShowShareModal(true);
    }
  };

  // Share functions
  const shareViaWhatsApp = (text) => {
    const encodedText = encodeURIComponent(text);
    window.open(`https://wa.me/?text=${encodedText}`, '_blank');
    setShowShareModal(false);
  };

  const shareViaFacebook = (url) => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    setShowShareModal(false);
  };

  const shareViaTwitter = (text) => {
    const encodedText = encodeURIComponent(text);
    window.open(`https://twitter.com/intent/tweet?text=${encodedText}`, '_blank');
    setShowShareModal(false);
  };

  const shareViaLinkedIn = (url) => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
    setShowShareModal(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Link copied to clipboard!');
      setShowShareModal(false);
    });
  };

  const activityData = [
    { date: 'Apr 24', icon: <Eye size={16} />, activity: 'CV Viewed', employer: 'ABC Agency', result: 'Viewed', status: 'viewed', color: 'primary' },
    { date: 'Apr 23', icon: <CheckCircle size={16} />, activity: 'Applied for', employer: 'XYZ Corp', result: 'Shortlisted', status: 'shortlisted', color: 'success' },
    { date: 'Apr 23', icon: <MessageCircle size={16} />, activity: 'Contact Unlocked', employer: 'Global Recruiters', result: 'Paid', status: 'paid', color: 'warning' },
    { date: 'Apr 22', icon: <CheckCircle size={16} />, activity: 'WhatsApp Call', employer: 'Happy Families', result: 'Completed', status: 'viewed', color: 'success' },
    { date: 'Apr 21', icon: <Eye size={16} />, activity: 'Employer Viewed CV', employer: 'DEF Ltd.', result: 'Viewed', status: 'viewed', color: 'primary' },
    { date: profileUpdatedDate || 'Today', icon: <CheckCircle size={16} />, activity: 'Profile Updated', employer: 'Basic Info & Experience', result: 'Upgrade', status: 'upgrade', color: 'success' }
  ];

  const handleEditProfile = () => {
    navigate('/personalinfo');
  };

  const handleViewProfile = () => {
    navigate('/combinedprofilecard');
  };

  const handleChangePassword = () => {
    navigate('/myaccount');
  };

  // Function to fetch profile data
  const fetchProfileData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      const userId = user.id || '';
      
      if (!userId) {
        throw new Error('User ID not found');
      }

      const response = await fetch(`${BaseUrl}/api/job-seeker/${userId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch profile data');
      }
      const data = await response.json();

      // Construct full URL for the profile photo if it exists
      if (data.profile_photo) {
        data.profile_photo = `${BaseUrl}${data.profile_photo}`;
      }

      setProfileData(data);
      return data;
    } catch (error) {
      console.error('Error fetching profile data:', error);
      throw error;
    }
  };

  // Function to download PDF
  const handleDownloadCV = async () => {
    try {
      setDownloadLoading(true);
      
      // Fetch profile data
      let data = profileData;
      if (!data) {
        data = await fetchProfileData();
      }

      if (!data) {
        alert('Unable to fetch profile data. Please try again.');
        return;
      }

      // Generate PDF
      const { pdf } = await import('@react-pdf/renderer');
      const ProfilePDF = (await import('../CombinedProfileCard/ProfilePDF')).default;
      const blob = await pdf(<ProfilePDF profileData={data} />).toBlob();
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${data.first_name}_${data.last_name}_Resume.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up URL
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to download CV. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className="candidate-dashboard-wrapper" style={{ backgroundColor: '#e8ebf0', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Top Navigation Bar */}
     
      <Navbar />

      {/* Welcome Section */}
       <div className="dashboard-main-container" style={{ backgroundColor: 'white', padding: '20px 24px', borderBottom: '1px solid #dee2e6', marginTop:"80px" }}>
        <div className="welcome-header-section d-flex justify-content-between align-items-center">
          <div className="welcome-user-info d-flex align-items-center gap-3">
            <img src="https://ui-avatars.com/api/?name=Zubair&background=3d6e6e&color=fff&size=56" alt="User" className="user-avatar-image" style={{ borderRadius: '50%', width: '56px', height: '56px' }} />
            <h2 className="welcome-user-name" style={{ margin: 0, fontSize: '24px', fontWeight: '500' }}>Welcome, Zubair</h2>
          </div>
          {/* <div className="notification-icons-group d-flex align-items-center gap-3">
            <div className="notification-bell-wrapper" style={{ position: 'relative' }}>
              <Bell size={24} color="#666" />
              <span className="notification-badge-count" style={{ position: 'absolute', top: '-5px', right: '-5px', backgroundColor: '#dc3545', color: 'white', borderRadius: '50%', width: '20px', height: '20px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>2</span>
            </div>
            <div className="notification-message-wrapper" style={{ position: 'relative' }}>
              <Bell size={24} color="#666" />
              <span className="notification-badge-count" style={{ position: 'absolute', top: '-5px', right: '-5px', backgroundColor: '#dc3545', color: 'white', borderRadius: '50%', width: '20px', height: '20px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>2</span>
            </div>
            <Menu size={24} color="#666" />
          </div> */}
        </div>

        {/* Action Buttons */}
        <div className="action-buttons-row d-flex gap-2 mt-4 flex-wrap">
          <button 
            className="action-button-edit d-flex align-items-center gap-2" 
            style={{ border: '1px solid #dee2e6', backgroundColor: 'white', padding: '10px 20px', borderRadius: '0px', fontSize: '14px' }}  
            onClick={handleEditProfile}
          >
            <Edit size={18} />
            Edit Profile
          </button>
          <button 
            className="action-button-view d-flex align-items-center gap-2" 
            style={{ border: '1px solid #dee2e6', backgroundColor: 'white', padding: '10px 20px', borderRadius: '0px', fontSize: '14px' }}  
            onClick={handleViewProfile}
          >
            <Eye size={18} />
            View CV
          </button>
          <button 
            className="action-button-download d-flex align-items-center gap-2" 
            style={{ 
              border: '1px solid #dee2e6', 
              backgroundColor: 'white', 
              padding: '10px 20px', 
              borderRadius: '0px', 
              fontSize: '14px',
              cursor: downloadLoading ? 'wait' : 'pointer',
              opacity: downloadLoading ? 0.7 : 1
            }}
            onClick={handleDownloadCV}
            disabled={downloadLoading}
          >
            <Download size={18} />
            {downloadLoading ? 'Generating PDF...' : 'Download CV'}
            {downloadLoading && (
              <span 
                className="spinner-border spinner-border-sm" 
                role="status" 
                aria-hidden="true"
                style={{ marginLeft: '5px', width: '12px', height: '12px' }}
              ></span>
            )}
          </button>
          <button 
            className="action-button-share d-flex align-items-center gap-2" 
            style={{ border: '1px solid #dee2e6', backgroundColor: 'white', padding: '10px 20px', borderRadius: '0px', fontSize: '14px' }}
            onClick={handleShareCV}
          >
            <Share2 size={18} />
            Share CV
          </button>
          {/* <button 
            className="action-button-activity d-flex align-items-center gap-2" 
            style={{ border: '1px solid #dee2e6', backgroundColor: 'white', padding: '10px 20px', borderRadius: '0px', fontSize: '14px', position: 'relative' }}
          >
        
            Activity
            <span className="activity-badge-indicator" style= {{backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: '0px', fontSize: '12px', marginLeft: '4px' }}>4</span>
          </button> */}
          <button 
            className="action-button-password d-flex align-items-center gap-2" 
            style={{ border: '1px solid #dee2e6', backgroundColor: 'white', padding: '10px 20px', borderRadius: '0px', fontSize: '14px', color: '#dc3545' }}
             onClick={handleChangePassword}
          >
            <Activity size={18} />

            Change Password
          </button>
        </div>
      </div> 

      {/* Main Content */}
      <div className="dashboard-content-container container-fluid" style={{ padding: '24px' }}>
        <div className="row">
          {/* Left Column - My Activity */}
          <div className="col-lg-8">
            <div className="activity-card-wrapper" style={{ backgroundColor: 'white', borderRadius: '0px', padding: '24px', marginBottom: '24px' }}>
              <div className="activity-card-header d-flex align-items-center gap-2 mb-3">
                <Users size={24} color="#3d6e6e" />
                <h3 className="activity-card-title" style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>My Activity</h3>
              </div>
              <p className="activity-card-subtitle" style={{ color: '#666', marginBottom: '24px' }}>Track your usage & limits</p>

              <div className="activity-section-container" style={{ backgroundColor: '#f8f9fa', borderRadius: '0px', padding: '20px' }}>
                <div className="activity-section-header d-flex justify-content-between align-items-center mb-3">
                  <h4 className="activity-section-title" style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>My Activity</h4>
                  <span className="activity-members-link" style={{ color: '#666', fontSize: '14px' }}>Umn all Members</span>
                </div>

                {/* Activity Items */}
                <div className="activity-items-list">
                 <div className="activity-item-row d-flex justify-content-between align-items-center mb-3">
  <div className="activity-item-left d-flex align-items-center gap-3" style={{ flex: 1 }}>
    <div className="activity-icon-circle" style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Eye size={18} color="#1976d2" />
    </div>
    <span className="activity-item-label">Profiles Visited</span>
  </div>
  <div className="activity-progress-bar" style={{ flex: 2, height: '8px', backgroundColor: '#e0e0e0', borderRadius: '0px', overflow: 'hidden', marginRight: '16px' }}>
    <div 
      className="activity-progress-fill" 
      style={{ 
        width: `${Math.min((profilesVisited.today / 20) * 100, 100)}%`, 
        height: '100%', 
        backgroundColor: '#ffc107', 
        borderRadius: '0px' 
      }}
    ></div>
  </div>
  <span className="activity-count-text" style={{ fontWeight: '600', minWidth: '60px' }}>
    {profilesVisited.today} / 20
  </span>
  <span className="activity-left-text" style={{ color: '#666', minWidth: '60px', textAlign: 'right' }}>
    {Math.max(20 - profilesVisited.today, 0)} left
  </span>
</div>

                  <div className="activity-item-row d-flex justify-content-between align-items-center mb-3">
                    <div className="activity-item-left d-flex align-items-center gap-3" style={{ flex: 1 }}>
                      <div className="activity-icon-circle" style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CheckCircle size={18} color="#4caf50" />
                      </div>
                      <span className="activity-item-label">Profiles Liked</span>
                    </div>
                    <div className="activity-progress-bar" style={{ flex: 2, height: '8px', backgroundColor: '#e0e0e0', borderRadius: '0px', overflow: 'hidden', marginRight: '16px' }}>
                      <div className="activity-progress-fill" style={{ width: '80%', height: '100%', backgroundColor: '#4caf50', borderRadius: '0px' }}></div>
                    </div>
                    <span className="activity-count-text" style={{ fontWeight: '600', minWidth: '60px' }}>8 / 10</span>
                    <span className="activity-left-text" style={{ color: '#666', minWidth: '60px', textAlign: 'right' }}>2 left</span>
                  </div>

                  {/* Replace the Messages Sent activity item with this code */}
<div className="activity-item-row d-flex justify-content-between align-items-center mb-3">
  <div className="activity-item-left d-flex align-items-center gap-3" style={{ flex: 1 }}>
    <div className="activity-icon-circle" style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#fff3e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <MessageCircle size={18} color="#ff9800" />
    </div>
    <span className="activity-item-label">Messages Received</span>
  </div>
  <div className="activity-progress-bar" style={{ flex: 2, height: '8px', backgroundColor: '#e0e0e0', borderRadius: '0px', overflow: 'hidden', marginRight: '16px' }}>
    <div 
      className="activity-progress-fill" 
      style={{ 
        width: `${loadingMessages ? '0%' : Math.min((messageCount / 5) * 100, 100)}%`, 
        height: '100%', 
        backgroundColor: '#ffc107', 
        borderRadius: '0px' 
      }}
    ></div>
  </div>
  <span className="activity-count-text" style={{ fontWeight: '600', minWidth: '60px' }}>
    {loadingMessages ? (
      <span 
        className="spinner-border spinner-border-sm" 
        role="status" 
        aria-hidden="true"
        style={{ width: '12px', height: '12px' }}
      ></span>
    ) : (
      `${messageCount} / 5`
    )}
  </span>
  <span className="activity-left-text" style={{ color: '#666', minWidth: '60px', textAlign: 'right' }}>
    {loadingMessages ? '' : `${Math.max(5 - messageCount, 0)} left`}
  </span>
</div>

                  <div className="activity-item-row d-flex justify-content-between align-items-center mb-3">
                    <div className="activity-item-left d-flex align-items-center gap-3" style={{ flex: 1 }}>
                      <div className="activity-icon-circle" style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#fce4ec', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Users size={18} color="#e91e63" />
                      </div>
                      <span className="activity-item-label">Contact Views</span>
                    </div>
                    <div className="activity-unlock-section d-flex align-items-center gap-3" style={{ flex: 3, justifyContent: 'flex-end' }}>
                      <div className="unlock-info-box d-flex align-items-center gap-2" style={{ backgroundColor: '#fff9e6', padding: '8px 16px', borderRadius: '0px' }}>
                        <Lock size={16} color="#ffc107" />
                        <span style={{ fontSize: '14px' }}>Unlock CV • 5 AED</span>
                      </div>
                      <button className="vos-button" style={{ border: '1px solid #3d6e6e', backgroundColor: 'white', color: '#3d6e6e', padding: '8px 16px', borderRadius: '0px', fontSize: '14px' }}>
                        Vos →
                      </button>
                      <button className="boost-again-button" style={{ backgroundColor: '#3d6e6e', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '0px', fontSize: '14px' }}>
                        Boost Again →
                      </button>
                    </div>
                  </div>

                  <div className="activity-item-row d-flex justify-content-between align-items-center">
                    <div className="activity-item-left d-flex align-items-center gap-3" style={{ flex: 1 }}>
                      <div className="activity-icon-circle" style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FileText size={18} color="#1976d2" />
                      </div>
                      <span className="activity-item-label">CV Unlocks</span>
                    </div>
                    <div className="activity-progress-bar" style={{ flex: 2, height: '8px', backgroundColor: '#e0e0e0', borderRadius: '0px', overflow: 'hidden', marginRight: '16px' }}>
                      <div className="activity-progress-fill" style={{ width: '40%', height: '100%', backgroundColor: '#ffc107', borderRadius: '0px' }}></div>
                    </div>
                    <span className="activity-count-text" style={{ fontWeight: '600', minWidth: '60px' }}>2 / 5</span>
                    <div className="limit-reached-section d-flex align-items-center gap-2" style={{ minWidth: '200px', justifyContent: 'flex-end' }}>
                      <span className="limit-reached-text" style={{ color: '#dc3545', fontSize: '14px' }}>Limit reached</span>
                      <span className="limit-divider" style={{ color: '#dee2e6' }}>|</span>
                      <button className="upgrade-premium-link" style={{ background: 'none', border: 'none', color: '#3d6e6e', fontSize: '14px', textDecoration: 'underline', cursor: 'pointer' }}>
                        Upgrade to Premium
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity Log */}
              <div className="activity-log-section mt-4">
                {/* My Activity Log Header with Tabs */}
                <div className="activity-log-header d-flex justify-content-between align-items-center mb-3">
                  <h4 className="activity-log-title" style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                    My Activity Log
                  </h4>
                  <div className="activity-log-tabs d-flex gap-3">
                    <button className="activity-tab-button activity-tab-active" style={{ 
                      background: 'none', 
                      border: 'none', 
                      padding: '8px 12px', 
                      fontWeight: '600', 
                      color: '#3d6e6e',
                      borderBottom: '2px solid #3d6e6e'
                    }}>
                      Today
                    </button>
                    <button className="activity-tab-button" style={{ 
                      background: 'none', 
                      border: 'none', 
                      padding: '8px 12px', 
                      color: '#666' 
                    }}>
                      This Week
                    </button>
                    <button className="activity-tab-button" style={{ 
                      background: 'none', 
                      border: 'none', 
                      padding: '8px 12px', 
                      color: '#666' 
                    }}>
                      Paid Activity
                    </button>
                    <button className="activity-tab-button" style={{ 
                      background: 'none', 
                      border: 'none', 
                      padding: '8px 12px', 
                      color: '#666' 
                    }}>
                      Employers Viewed
                    </button>
                    <button className="activity-tab-button" style={{ 
                      background: 'none', 
                      border: 'none', 
                      padding: '8px 12px', 
                      color: '#666' 
                    }}>
                      All Time
                    </button>
                  </div>
                </div>

                <div className="activity-log-table-wrapper" style={{ overflowX: 'auto' }}>
                  <table className="activity-log-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0' }}>
                    <thead>
                      <tr className="activity-log-header-row">
                        <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>Date</th>
                        <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>Activity</th>
                        <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>Employer / Job</th>
                        <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>Result</th>
                        <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                          <button className="view-all-activity-link" style={{ background: 'none', border: 'none', color: '#3d6e6e', fontSize: '14px', cursor: 'pointer' }}>
                            View All Activity
                          </button>
                        </th>
                        
                      </tr>
                    </thead>
                    <tbody>
                      {/* Static activities (keep your existing static ones) */}
                      {activityData.slice(0, 1).map((item, index) => (
                        <tr key={`static-${index}`} className="activity-log-data-row" style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td className="activity-log-td" style={{ padding: '14px 16px', fontSize: '14px', color: '#333' }}>{item.date}</td>
                          <td className="activity-log-td" style={{ padding: '14px 16px' }}>
                            <div className="activity-log-cell d-flex align-items-center gap-2">
                              <div className="activity-log-icon" style={{ 
                                width: '32px', 
                                height: '32px', 
                                borderRadius: '50%', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                backgroundColor: '#e3f2fd',
                                flexShrink: 0 
                              }}>
                                <MessageCircle size={16} color="#1976d2" />
                              </div>
                              <span style={{ fontSize: '14px', color: '#333' }}>{item.activity}</span>
                            </div>
                          </td>
                          <td className="activity-log-td" style={{ padding: '14px 16px', fontSize: '14px', color: '#333' }}>{item.employer}</td>
                          <td className="activity-log-td" style={{ padding: '14px 16px' }}>
                            <span className="activity-result-badge" style={{ fontSize: '14px', color: '#666' }}>
                              ● {item.result}
                            </span>
                          </td>
                          <td className="activity-log-td" style={{ padding: '14px 16px', textAlign: 'right' }}>
                            <span className="activity-status-viewed" style={{ 
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              padding: '6px 14px',
                              borderRadius: '0px',
                              fontSize: '13px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontWeight: '500'
                            }}>
                              <Eye size={14} />
                              {item.result === 'Upgrade' ? 'Upgrade' : item.result}
                            </span>
                          </td>
                        </tr>
                      ))}

                      {/* Dynamic "Applied for" activities from API */}
                      {loadingApplications ? (
                        <tr>
                          <td colSpan="5" style={{ padding: '20px', textAlign: 'center' }}>
                            <span 
                              className="spinner-border spinner-border-sm" 
                              role="status" 
                              aria-hidden="true"
                              style={{ marginRight: '5px', width: '12px', height: '12px' }}
                            ></span>
                            Loading applications...
                          </td>
                        </tr>
                      ) : applicationsData.length > 0 ? (
                        applicationsData.slice(0, 5).map((application, index) => {
                          // Format the date
                          const appDate = new Date(application.application_date);
                          const formattedDate = appDate.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          });
                          
                          // Get job title from employer jobs
                          const jobTitle = getJobTitleByJobId(application.jobid);
                          
                          return (
                            <tr key={`app-${index}`} className="activity-log-data-row" style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td className="activity-log-td" style={{ padding: '14px 16px', fontSize: '14px', color: '#333' }}>
                                {formattedDate}
                              </td>
                              <td className="activity-log-td" style={{ padding: '14px 16px' }}>
                                <div className="activity-log-cell d-flex align-items-center gap-2">
                                  <div className="activity-log-icon" style={{ 
                                    width: '32px', 
                                    height: '32px', 
                                    borderRadius: '50%', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    backgroundColor: '#e8f5e9',
                                    flexShrink: 0 
                                  }}>
                                    <CheckCircle size={16} color="#4caf50" />
                                  </div>
                                  <span style={{ fontSize: '14px', color: '#333' }}>Applied for</span>
                                </div>
                              </td>
                              <td className="activity-log-td" style={{ padding: '14px 16px', fontSize: '14px', color: '#333' }}>
                                {/* Display job title from employer table */}
                                {jobTitle}
                              </td>
                              <td className="activity-log-td" style={{ padding: '14px 16px' }}>
                                <span className="activity-result-badge" style={{ 
                                  fontSize: '14px', 
                                  color: application.status === 'Shortlisted' ? '#4caf50' : 
                                         application.status === 'Rejected' ? '#dc3545' : '#666'
                                }}>
                                  ● {application.status}
                                </span>
                              </td>
                              <td className="activity-log-td" style={{ padding: '14px 16px', textAlign: 'right' }}>
                                <span className="activity-status-viewed" style={{ 
                                  backgroundColor: application.status === 'Shortlisted' ? '#e8f5e9' : 
                                                 application.status === 'Rejected' ? '#fdeaea' : '#e3f2fd',
                                  color: application.status === 'Shortlisted' ? '#4caf50' : 
                                         application.status === 'Rejected' ? '#dc3545' : '#1976d2',
                                  padding: '6px 14px',
                                  borderRadius: '0px',
                                  fontSize: '13px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontWeight: '500'
                                }}>
                                  {application.status === 'Shortlisted' ? 'Shortlisted' : 
                                   application.status === 'Rejected' ? 'Rejected' : 'Applied'}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            No applications found
                          </td>
                        </tr>
                      )}

                      {/* Rest of static activities (skip the "Applied for" static one) */}
                      {activityData.slice(2).map((item, index) => (
                        <tr key={`static-rest-${index}`} className="activity-log-data-row" style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td className="activity-log-td" style={{ padding: '14px 16px', fontSize: '14px', color: '#333' }}>{item.date}</td>
                          <td className="activity-log-td" style={{ padding: '14px 16px' }}>
                            <div className="activity-log-cell d-flex align-items-center gap-2">
                              <div className="activity-log-icon" style={{ 
                                width: '32px', 
                                height: '32px', 
                                borderRadius: '50%', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                backgroundColor: '#e3f2fd',
                                flexShrink: 0 
                              }}>
                                <MessageCircle size={16} color="#1976d2" />
                              </div>
                              <span style={{ fontSize: '14px', color: '#333' }}>{item.activity}</span>
                            </div>
                          </td>
                          <td className="activity-log-td" style={{ padding: '14px 16px', fontSize: '14px', color: '#333' }}>{item.employer}</td>
                          <td className="activity-log-td" style={{ padding: '14px 16px' }}>
                            <span className="activity-result-badge" style={{ fontSize: '14px', color: '#666' }}>
                              ● {item.result}
                            </span>
                          </td>
                          <td className="activity-log-td" style={{ padding: '14px 16px', textAlign: 'right' }}>
                            <span className="activity-status-viewed" style={{ 
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              padding: '6px 14px',
                              borderRadius: '0px',
                              fontSize: '13px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontWeight: '500'
                            }}>
                              <Eye size={14} />
                              {item.result === 'Upgrade' ? 'Upgrade' : item.result}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Upgrade Section Below Table */}
                <div className="upgrade-section mt-3" style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '16px', 
                  borderRadius: '0px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  {/* Upgrade section content removed as per original */}
                </div>
              </div>
            </div>
            {/* Profile Viewers Section */}
            <div className="viewers-card-wrapper mt-4" style={{ backgroundColor: 'white', borderRadius: '0px', padding: '24px', marginBottom: '24px' }}>
              <div className="viewers-card-header d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-2">
                  <Users size={24} color="#3d6e6e" />
                  <h3 className="viewers-card-title" style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>See Who Viewed Your Profile</h3>
                </div>
                {!isPremiumPlan() && (
                  <span style={{ 
                    backgroundColor: '#fff3e0', 
                    color: '#ff9800', 
                    padding: '4px 12px', 
                    fontSize: '12px', 
                    fontWeight: '600' 
                  }}>PREMIUM</span>
                )}
              </div>

              <div className="viewers-section-container" style={{ position: 'relative', minHeight: '200px' }}>
                {!isPremiumPlan() ? (
                  <div 
                    className="locked-viewers-overlay" 
                    onClick={() => handleLockedFeatureClick('who viewed your profile')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div style={{ filter: 'blur(5px)', opacity: 0.4, pointerEvents: 'none' }}>
                      <div className="activity-log-table-wrapper">
                        <table className="activity-log-table" style={{ width: '100%' }}>
                          <thead>
                            <tr className="activity-log-header-row">
                              <th className="activity-log-th">S.No</th>
                              <th className="activity-log-th">Viewer</th>
                              <th className="activity-log-th">Type</th>
                              <th className="activity-log-th">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[1, 2, 3].map((_, i) => (
                              <tr key={i} className="activity-log-data-row">
                                <td className="activity-log-td">{i + 1}</td>
                                <td className="activity-log-td">Sample Employer</td>
                                <td className="activity-log-td">Employer</td>
                                <td className="activity-log-td">Jan 01, 2024</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 2,
                      textAlign: 'center',
                      background: 'white',
                      padding: '30px',
                      borderRadius: '0px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                      border: '1px solid #dee2e6',
                      width: '80%',
                      maxWidth: '400px'
                    }}>
                      <div className="lock-icon-circle mb-3" style={{ 
                        width: '60px', 
                        height: '60px', 
                        borderRadius: '50%', 
                        backgroundColor: '#f8f9fa', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        margin: '0 auto'
                      }}>
                        <Lock size={30} color="#3d6e6e" />
                      </div>
                      <h4 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>Feature Locked</h4>
                      <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>Upgrade to Silver or Gold to see which employers are visiting your profile.</p>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleLockedFeatureClick('who viewed your profile'); }}
                        style={{ 
                          backgroundColor: '#3d6e6e', 
                          color: 'white', 
                          border: 'none', 
                          padding: '12px 24px', 
                          borderRadius: '0px', 
                          fontWeight: '600'
                        }}
                      >
                        Upgrade Now
                      </button>
                    </div>
                  </div>
                ) : viewersLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : profileViewers.length > 0 ? (
                  <div className="activity-log-table-wrapper" style={{ overflowX: 'auto' }}>
                    <table className="activity-log-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0' }}>
                      <thead>
                        <tr className="activity-log-header-row">
                          <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>S.No</th>
                          <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>Viewer Name</th>
                          <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>Email</th>
                          <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>Phone</th>
                          <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>User Type</th>
                          <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>Viewed</th>
                          <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>Date & Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[1, 2, 3].map((_, i) => (
                          <tr key={i} className="activity-log-data-row" style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td className="activity-log-td" style={{ padding: '14px 16px', fontSize: '14px', color: '#333' }}>{i + 1}</td>
                            <td className="activity-log-td fw-bold" style={{ padding: '14px 16px', fontSize: '14px', color: '#333' }}>Sample Name</td>
                            <td className="activity-log-td" style={{ padding: '14px 16px' }}>
                              <div style={{ background: '#e1f5fe', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
                                <MailIcon size={14} className="text-primary" />
                              </div>
                            </td>
                            <td className="activity-log-td" style={{ padding: '14px 16px' }}>
                               <div style={{ background: '#e8f5e9', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
                                <PhoneIcon size={14} className="text-success" />
                              </div>
                            </td>
                            <td className="activity-log-td" style={{ padding: '14px 16px', fontSize: '14px', color: '#333' }}>Agency</td>
                            <td className="activity-log-td" style={{ padding: '14px 16px', fontSize: '14px', color: '#333' }}>Profile</td>
                            <td className="activity-log-td" style={{ padding: '14px 16px', fontSize: '14px', color: '#333', textAlign: 'right' }}>Jan 01, 2024 | 10:00 AM</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : viewersLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : profileViewers.length > 0 ? (
                  <div className="activity-log-table-wrapper" style={{ overflowX: 'auto' }}>
                    <table className="activity-log-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0' }}>
                      <thead>
                        <tr className="activity-log-header-row">
                          <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>S.No</th>
                          <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>Viewer Name</th>
                          <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>Email</th>
                          <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>Phone</th>
                          <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>User Type</th>
                          <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>Viewed</th>
                          <th className="activity-log-th" style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>Date & Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profileViewers.map((viewer, index) => (
                          <tr key={viewer.id} className="activity-log-data-row" style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td className="activity-log-td" style={{ padding: '14px 16px', fontSize: '14px', color: '#333' }}>{index + 1}</td>
                            <td className="activity-log-td" style={{ padding: '14px 16px', fontSize: '14px', color: '#333', fontWeight: '600' }}>{viewer.viewer_name || 'Anonymous'}</td>
                            <td className="activity-log-td" style={{ padding: '14px 16px' }}>
                              {viewer.viewer_email ? (
                                <div 
                                  style={{ background: '#e1f5fe', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', cursor: 'pointer' }} 
                                  title={viewer.viewer_email} 
                                  onClick={() => window.location.href = `mailto:${viewer.viewer_email}`}
                                >
                                  <MailIcon size={14} className="text-primary" />
                                </div>
                              ) : 'N/A'}
                            </td>
                            <td className="activity-log-td" style={{ padding: '14px 16px' }}>
                              {viewer.viewer_phone ? (
                                <div 
                                  style={{ background: '#e8f5e9', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', cursor: 'pointer' }} 
                                  title={viewer.viewer_phone} 
                                  onClick={() => window.location.href = `tel:${viewer.viewer_phone}`}
                                >
                                  <PhoneIcon size={14} className="text-success" />
                                </div>
                              ) : 'N/A'}
                            </td>
                            <td className="activity-log-td" style={{ padding: '14px 16px' }}>
                              <span style={{ 
                                backgroundColor: '#e3f2fd', 
                                color: '#1976d2', 
                                padding: '4px 10px', 
                                fontSize: '12px', 
                                borderRadius: '0px',
                                textTransform: 'capitalize'
                              }}>{viewer.viewer_user_type || 'User'}</span>
                            </td>
                            <td className="activity-log-td" style={{ padding: '14px 16px', fontSize: '14px', color: '#666', textTransform: 'capitalize' }}>
                              {viewer.view_type}
                            </td>
                            <td className="activity-log-td" style={{ padding: '14px 16px', fontSize: '14px', color: '#333', textAlign: 'right' }}>
                              {new Date(viewer.viewed_at).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <Eye size={40} className="mb-2 opacity-25" />
                    <p>No profile views yet. Your profile is visible to employers!</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Current Plan */}
          <div className="col-lg-4">
            {/* First Box - My Current Plan (Free) */}
            <div className="plan-card-wrapper" style={{ backgroundColor: 'white', borderRadius: '0px', padding: '24px', marginBottom: '16px' }}>
              <h3 className="plan-card-title" style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>My Current Plan</h3>
              <p className="plan-card-type" style={{ color: '#666', marginBottom: '20px' }}>Free</p>

              <div className="plan-payperuse-section" style={{ border: '1px solid #e0e0e0', borderRadius: '0px', padding: '16px', marginBottom: '16px' }}>
                <div className="plan-payperuse-header d-flex align-items-center gap-2 mb-2">
                  <div className="plan-check-icon" style={{ width: '20px', height: '20px', borderRadius: '50%', border: '2px solid #3d6e6e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircle size={14} color="#3d6e6e" />
                  </div>
                  <span className="plan-payperuse-label" style={{ fontWeight: '600' }}>Pay-Per-Use</span>
                  <span className="plan-cv-badge" style={{ backgroundColor: '#f5f5f5', padding: '2px 8px', borderRadius: '0px', fontSize: '12px', marginLeft: 'auto' }}>CV 1.5P$</span>
                </div>
                <p className="plan-price-text" style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>
                  5 AED / CV <span style={{ fontSize: '14px', fontWeight: '400', color: '#666' }}>($1.30)</span>
                </p>
              </div>

              <button className="unlock-cv-button w-100" style={{ backgroundColor: '#3d6e6e', color: 'white', border: 'none', padding: '14px', borderRadius: '0px', fontSize: '16px', fontWeight: '600' }}>
                Unlock CV
              </button>
            </div>

            {/* Second Box - My Current Plan with Premium */}
            <div className="plan-card-wrapper-second" style={{ backgroundColor: 'white', borderRadius: '0px', padding: '24px', marginBottom: '24px' }}>
              <h4 className="plan-current-title" style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>My Current Plan</h4>

              <div className="plan-current-details">
                <div className="plan-current-payperuse d-flex align-items-center gap-2 mb-2">
                  <div className="plan-current-check-icon" style={{ width: '20px', height: '20px', borderRadius: '50%', border: '2px solid #3d6e6e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircle size={14} color="#3d6e6e" />
                  </div>
                  <span className="plan-current-label" style={{ fontWeight: '600' }}>Pay-Per-Use</span>
                </div>
                <p className="plan-current-price" style={{ fontSize: '16px', margin: 0, marginBottom: '16px' }}>
                  5 AED / CV <span style={{ fontSize: '14px', color: '#666' }}>($1.30)</span>
                </p>

                <div className="plan-premium-highlight" style={{ backgroundColor: '#fffbf0', border: '1px solid #ffe082', borderRadius: '0px', padding: '12px', marginBottom: '16px' }}>
                  <div className="plan-premium-price d-flex align-items-center gap-2">
                    <Star size={16} color="#ffc107" fill="#ffc107" />
                    <span style={{ fontWeight: '600' }}>900 AED</span>
                    <span style={{ color: '#666', fontSize: '14px' }}>($246) – 15 Days</span>
                  </div>
                </div>

                <div className="plan-premium-card" style={{ border: '2px solid #ffe082', borderRadius: '0px', padding: '20px', marginBottom: '20px', position: 'relative' }}>
                  <div className="plan-ribbon" style={{ position: 'absolute', top: '12px', right: '12px', color: '#ffc107' }}>
                    <Star size={20} fill="#ffc107" />
                  </div>
                  <h5 className="plan-premium-title" style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>Premium</h5>
                  <p className="plan-premium-price-large" style={{ fontSize: '20px', fontWeight: '700', marginBottom: '12px' }}>
                    900 AED <span style={{ fontSize: '14px', fontWeight: '400', color: '#666' }}>($246)</span>
                  </p>
                  <p className="plan-premium-benefits" style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                    Get Unlimited CV Unlocks & <span style={{ fontWeight: '600' }}>Joat (Priority</span>
                  </p>
                  <p className="plan-premium-savings" style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                    <span style={{ fontWeight: '600' }}>Save: 225 AED</span> ★ Pay-Per-Use
                  </p>
                  <button className="upgrade-premium-button w-100" style={{ backgroundColor: '#3d6e6e', color: 'white', border: 'none', padding: '12px', borderRadius: '0px', fontSize: '15px', fontWeight: '600' }}>
                    Upgrade to Premium
                  </button>
                </div>

                <a href="https://wa.me/97450783800" target="_blank" rel="noopener noreferrer" className="contact-whatsapp-link d-flex align-items-center justify-content-center gap-2" style={{ 
                  backgroundColor: '#25d366', 
                  color: 'white', 
                  padding: '14px', 
                  borderRadius: '0px', 
                  textDecoration: 'none', 
                  fontSize: '16px', 
                  fontWeight: '600' 
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  +974 5078 3800
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="share-modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="share-modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '0px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div className="share-modal-header d-flex justify-content-between align-items-center mb-3">
              <h3 className="share-modal-title" style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Share CV</h3>
              <button 
                onClick={() => setShowShareModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>
            
            <div className="share-options mb-4">
              <p className="mb-3" style={{ color: '#666' }}>Share this CV via:</p>
              
              <div className="d-flex justify-content-center gap-3 mb-4">
                <button 
                  onClick={() => shareViaWhatsApp(shareContent)}
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: '#25d366',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </button>
                
                <button 
                  onClick={() => shareViaFacebook(shareUrl)}
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: '#1877f2',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </button>
                
                <button 
                  onClick={() => shareViaTwitter(shareContent)}
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: '#1da1f2',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.213c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </button>
                
                <button 
                  onClick={() => shareViaLinkedIn(shareUrl)}
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: '#0077b5',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </button>
                
                <button 
                  onClick={() => copyToClipboard(shareUrl)}
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: '#6c757d',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <Share2 size={20} color="white" />
                </button>
              </div>
            </div>
            
            <div className="share-text mb-3">
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Share this text:</label>
              <textarea 
                value={shareContent}
                readOnly
                onClick={(e) => e.target.select()}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #dee2e6',
                  borderRadius: '0px',
                  fontSize: '14px',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
              />
            </div>
            
            <div className="share-modal-actions d-flex gap-2">
              <button 
                onClick={() => setShowShareModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: '1px solid #dee2e6',
                  backgroundColor: 'white',
                  borderRadius: '0px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={() => copyToClipboard(shareContent)}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  backgroundColor: '#3d6e6e',
                  color: 'white',
                  borderRadius: '0px',
                  cursor: 'pointer'
                }}
              >
                Copy Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateDashboard;
